import type { ShortcutPlatform } from '@/lib/keyboard/types/shortcut-platform'
import { getShortcutPlatform } from '@/lib/keyboard/keyboard-shortcuts'
import type { TextEditorShortcutKey } from './types/text-editor-shortcut-key'
import { textEditorShortcuts } from './constants/text-editor-shortcut-registry'

type MinimalKeyEvent = Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'metaKey' | 'shiftKey' | 'altKey'>

/**
 * Returns true when a keyboard event matches the given text-editor shortcut combo.
 * Handles cross-platform `mod` (Cmd on Mac, Ctrl on Windows). Key comparison is case-insensitive
 * so `e.key = 'c'` matches the token `'C'` in the combo definition.
 */
export function matchesTextEditorShortcut(
  e: MinimalKeyEvent,
  key: TextEditorShortcutKey,
  platform: ShortcutPlatform = getShortcutPlatform()
): boolean {
  const isMod = platform === 'mac' ? e.metaKey : e.ctrlKey

  for (const combo of textEditorShortcuts[key][platform]) {
    const c = combo as readonly string[]
    const needsMod = c.includes('mod')
    const needsShift = c.includes('shift')
    const needsAlt = c.includes('alt')
    const mainKey = c.find((t) => t !== 'mod' && t !== 'shift' && t !== 'alt') ?? null

    if (needsMod !== isMod) continue
    if (needsShift !== e.shiftKey) continue
    if (needsAlt !== e.altKey) continue
    if (mainKey !== null && e.key !== mainKey && e.key.toLowerCase() !== mainKey.toLowerCase())
      continue

    return true
  }

  return false
}
